import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { setSEO } from "@/lib/api";
import VoidAtmosphere from "@/components/VoidAtmosphere";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setSEO({ title: "Admin Access | FEMBOT NEX", description: "Restricted." });
  }, []);

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-32 font-mono text-xs tracking-[0.2em] uppercase text-[#6b6b6b]">
        // checking session...
      </div>
    );
  }

  if (user) return <Navigate to="/admin" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(form.email, form.password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Authentication failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="admin-login-page" className="max-w-[1400px] mx-auto px-4 md:px-8 py-20 md:py-32 relative">
      <VoidAtmosphere intensity="subtle" />
      <div className="max-w-md mx-auto relative">
        <div className="flex flex-col items-center gap-4 mb-4">
          <img
            src="/brand/emblem.png"
            alt="FEMBOT NEX emblem"
            width={64}
            height={64}
            className="drop-shadow-[0_0_22px_rgba(255,20,147,0.65)]"
            draggable={false}
          />
          <span className="badge badge-locked"><Lock size={10} strokeWidth={1.5} /> Restricted</span>
        </div>
        <h1 className="text-center font-display text-4xl md:text-5xl tracking-tight">Admin Access.</h1>
        <p className="text-center mt-3 font-mono text-[11px] tracking-[0.2em] uppercase text-[#c084fc]">
          // POST HOMINEM · Authorized personnel only
        </p>

        <div className="mt-10 border border-[#262626] bg-black p-6 md:p-8">
          <form onSubmit={onSubmit} className="flex flex-col gap-4" data-testid="admin-login-form">
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#ff1493]">// Email</label>
              <input
                data-testid="admin-login-email"
                required
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="field mt-2"
                placeholder="admin@fembotnex.com"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#ff1493]">// Password</label>
              <input
                data-testid="admin-login-password"
                required
                type="password"
                autoComplete="current-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="field mt-2"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={submitting}
              className="btn-primary justify-center"
            >
              {submitting ? "Authenticating…" : "Authorize"} <ArrowRight size={14} strokeWidth={1.5} />
            </button>
            {error && (
              <p
                data-testid="admin-login-error"
                className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#ff3b30]"
              >
                // {error}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
